'use client';

import { automationId } from '@terra/shared-utils';
import { Box } from '@terra/ui-styled-system';
import { Drawer } from '../../components/drawer';
import { Tabs } from '../../components/tabs';
import {
  CustomDrawerHeader,
  type HeaderConfig,
  type IconConfig,
} from './CustomDrawerHeader';

export type { IconConfig, HeaderConfig };

type TabConfig = {
  value: string;
  label: string;
};

const DEFAULT_WIDTH = '85vw';
const DEFAULT_MAX_WIDTH = '1200px';

export type CustomDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  header: HeaderConfig;
  tabs?: TabConfig[];
  selectedTab?: string;
  onTabChange?: (tab: string) => void;
  children: React.ReactNode;
  width?: string;
  maxWidth?: string;
  density?: 'default' | 'compact';
  dataTestid?: string;
};

export function CustomDrawer({
  isOpen,
  onClose,
  header,
  tabs,
  selectedTab,
  onTabChange,
  children,
  width = DEFAULT_WIDTH,
  maxWidth = DEFAULT_MAX_WIDTH,
  density = 'default',
  dataTestid = '',
}: CustomDrawerProps) {
  const hasTabs = tabs && tabs.length > 0;

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
      lazyMount
      unmountOnExit
    >
      <Drawer.Backdrop />
      <Drawer.Positioner w={width} maxW={maxWidth}>
        <Drawer.Content
          display="flex"
          flexDirection="column"
          h="full"
          {...automationId(dataTestid)}
        >
          <CustomDrawerHeader
            {...header}
            density={density}
            dataTestid={dataTestid}
          />

          {hasTabs && (
            <Box w="full" flexShrink={0} borderTopWidth={0}>
              <Tabs.Root
                value={selectedTab}
                boxShadow={undefined}
                onValueChange={(e: { value?: string }) =>
                  onTabChange?.(e.value ?? '')
                }
              >
                <Tabs.List px={8} gap={0}>
                  {tabs.map((tab) => (
                    <Tabs.Trigger
                      key={tab.value}
                      value={tab.value}
                      fontWeight="medium"
                      {...automationId(
                        dataTestid,
                        `tab-${tab.value.toLowerCase()}`,
                      )}
                    >
                      {' '}
                      {tab.label}
                    </Tabs.Trigger>
                  ))}
                  <Tabs.Indicator />
                </Tabs.List>
              </Tabs.Root>
            </Box>
          )}

          <Drawer.Body
            pt={density === 'compact' ? 6 : 8}
            px={density === 'compact' ? 6 : 9}
            pb={density === 'compact' ? 6 : 8}
            flex={1}
            overflow="auto"
            borderTopWidth={hasTabs ? 0 : 1}
            borderTopColor="border.subtle"
          >
            {children}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
